import { motion } from "framer-motion";
import { Shield, Lock, Eye, Trash2, Mail, Download, Globe, Scale } from "lucide-react";
import { pageVariants } from "../components/app/constants";
import { SEO } from "../components/shared/SEO";

const Section = ({ icon: Icon, title, children }) => (
  <div className="mb-10">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center">
        <Icon className="w-5 h-5 text-[#D4AF37]" />
      </div>
      <h2 className="font-serif text-2xl text-white">{title}</h2>
    </div>
    <div className="text-white/70 text-sm leading-relaxed space-y-3 pl-[52px]">
      {children}
    </div>
  </div>
);

export const PrivacyPage = () => {
  const effectiveDate = "22 April 2026";

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="min-h-screen pt-32 pb-20 px-6"
      data-testid="privacy-page"
    >
      <SEO
        title="Privacy Policy — StarFrame AI"
        path="/privacy"
        description="StarFrame AI Privacy Policy — GDPR-compliant data processing disclosure, user rights under Swedish and EU law, data retention, third-party processors."
      />

      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-12 pb-8 border-b border-white/10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Legal</p>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-white mb-4">
            Privacy Policy
          </h1>
          <p className="text-white/60 text-sm">
            Effective date: <span className="text-white/80">{effectiveDate}</span>
          </p>
          <p className="text-white/50 text-xs mt-2">
            This policy complies with the EU General Data Protection Regulation (GDPR 2016/679),
            the Swedish Data Protection Act (Dataskyddslagen, SFS 2018:218), and the ePrivacy
            Directive as implemented by the Swedish Electronic Communications Act.
          </p>
        </div>

        {/* 1. Data Controller */}
        <Section icon={Shield} title="1. Who We Are (Data Controller)">
          <p>
            StarFrame AI is operated by <strong className="text-white">MITA ICT AB</strong>,
            a company registered in Sweden.
          </p>
          <ul className="space-y-1 pl-5 list-disc marker:text-[#D4AF37]">
            <li>Controller: MITA ICT AB</li>
            <li>Country: Sweden</li>
            <li>Contact email: <a href="mailto:info@mitaict.com" className="text-[#D4AF37] hover:underline">info@mitaict.com</a></li>
            <li>Website: <a href="https://www.mitaict.com" className="text-[#D4AF37] hover:underline" target="_blank" rel="noopener noreferrer">www.mitaict.com</a></li>
          </ul>
          <p>
            For all privacy questions, access/erasure requests, or concerns about how your
            personal data is processed, please email us at the address above. We respond within
            30 days as required by Article 12(3) GDPR.
          </p>
        </Section>

        {/* 2. What We Collect */}
        <Section icon={Eye} title="2. What Personal Data We Process">
          <p><strong className="text-white">Account data:</strong> name, email address, password hash (bcrypt), profile picture (if Google sign-in), referral code, account creation date.</p>
          <p><strong className="text-white">Photos you upload:</strong> selfies/photos you submit for AI portrait generation. These are considered biometric-adjacent data under GDPR Article 9 and are handled with extra care.</p>
          <p><strong className="text-white">Generated portraits:</strong> the AI-generated images we create for you, plus thumbnails stored in your gallery.</p>
          <p><strong className="text-white">Payment metadata:</strong> Stripe Customer ID, transaction IDs, purchase history, subscription status. <strong className="text-white">We never see or store your card number</strong> — card data stays with Stripe.</p>
          <p><strong className="text-white">Usage data:</strong> portraits generated, credits used, login timestamps, device type, browser, IP address (for fraud prevention and analytics).</p>
          <p><strong className="text-white">Cookies:</strong> authentication cookies (HttpOnly, Secure, SameSite=None), and minimal analytics. See Section 9.</p>
          <p><strong className="text-white">Marketing Agent users only:</strong> LinkedIn access tokens, published post content, and post analytics (impressions, likes, comments) when you connect LinkedIn.</p>
        </Section>

        {/* 3. Legal Basis */}
        <Section icon={Scale} title="3. Legal Basis for Processing (GDPR Art. 6)">
          <p><strong className="text-white">Contract (Art. 6(1)(b)):</strong> We process your account data, uploads, and generated portraits to deliver the service you signed up for.</p>
          <p><strong className="text-white">Consent (Art. 6(1)(a) & Art. 9(2)(a)):</strong> When you upload a photo of yourself, you provide explicit consent for us to process that image through AI generation. You can withdraw consent at any time by deleting the photo or your account.</p>
          <p><strong className="text-white">Legal obligation (Art. 6(1)(c)):</strong> Tax, accounting, and anti-fraud records required under Swedish law (Bokföringslagen requires 7-year retention of financial records).</p>
          <p><strong className="text-white">Legitimate interest (Art. 6(1)(f)):</strong> Service security, fraud prevention, basic usage analytics, and improving the product. You can object at any time.</p>
        </Section>

        {/* 4. Third Parties */}
        <Section icon={Globe} title="4. Third-Party Processors (Sub-processors)">
          <p>
            We use carefully selected service providers who act as data processors on our behalf.
            All of them have signed Data Processing Agreements (DPAs) and comply with GDPR:
          </p>
          <ul className="space-y-2 pl-5 list-disc marker:text-[#D4AF37]">
            <li><strong className="text-white">Stripe Payments Europe, Ltd.</strong> (Ireland) — payment processing. Cards never touch our servers.</li>
            <li><strong className="text-white">SendGrid / Twilio Inc.</strong> (USA) — transactional email. Data transferred under Standard Contractual Clauses (SCC).</li>
            <li><strong className="text-white">Google LLC</strong> (USA) — Gemini AI image generation, Google Sign-In. Transferred under SCC.</li>
            <li><strong className="text-white">OpenAI</strong> (USA) — Marketing Agent text generation only. Transferred under SCC.</li>
            <li><strong className="text-white">LinkedIn Ireland Unlimited Company</strong> (Ireland) — when you opt in to connect LinkedIn from the Marketing Agent.</li>
            <li><strong className="text-white">MongoDB Atlas</strong> (EU region) — primary database.</li>
            <li><strong className="text-white">Cloudflare, Inc.</strong> (USA) — CDN, DDoS protection.</li>
            <li><strong className="text-white">Emergent Agent Ltd.</strong> — hosting platform.</li>
          </ul>
          <p>
            For non-EU transfers (USA), we rely on European Commission Standard Contractual
            Clauses (SCC) per Article 46(2)(c) GDPR. You may request a copy of the safeguards.
          </p>
        </Section>

        {/* 5. Data Retention */}
        <Section icon={Lock} title="5. How Long We Keep Your Data">
          <p><strong className="text-white">Uploaded photos:</strong> deleted automatically 30 days after the generation job completes, unless you have an active Vault subscription.</p>
          <p><strong className="text-white">Generated portraits (free tier):</strong> 30 days in your gallery, then automatically deleted.</p>
          <p><strong className="text-white">Vault subscribers:</strong> your portraits are retained for as long as your subscription is active, and for 14 days after cancellation (grace period for re-activation).</p>
          <p><strong className="text-white">Account data:</strong> kept while your account is active. Deleted within 30 days of account closure, except where legal obligation requires retention (tax records: 7 years per Bokföringslagen).</p>
          <p><strong className="text-white">Login logs:</strong> 90 days for security/fraud prevention.</p>
          <p><strong className="text-white">Payment records:</strong> 7 years (Swedish accounting law).</p>
          <p><strong className="text-white">LinkedIn tokens:</strong> deleted immediately when you disconnect LinkedIn.</p>
        </Section>

        {/* 6. Your Rights */}
        <Section icon={Download} title="6. Your Rights Under GDPR">
          <p>You have the following rights regarding your personal data:</p>
          <ul className="space-y-2 pl-5 list-disc marker:text-[#D4AF37]">
            <li><strong className="text-white">Right of access (Art. 15):</strong> request a copy of all personal data we hold about you.</li>
            <li><strong className="text-white">Right to rectification (Art. 16):</strong> correct inaccurate data.</li>
            <li><strong className="text-white">Right to erasure / "right to be forgotten" (Art. 17):</strong> delete your account and all associated data.</li>
            <li><strong className="text-white">Right to restriction (Art. 18):</strong> pause processing in certain circumstances.</li>
            <li><strong className="text-white">Right to data portability (Art. 20):</strong> export your portraits and account data in a machine-readable format (JSON / original image files).</li>
            <li><strong className="text-white">Right to object (Art. 21):</strong> object to processing based on legitimate interest.</li>
            <li><strong className="text-white">Rights regarding automated decision-making (Art. 22):</strong> we do not perform automated decision-making with legal effects.</li>
            <li><strong className="text-white">Right to withdraw consent (Art. 7(3)):</strong> withdraw consent any time without affecting past lawful processing.</li>
          </ul>
          <p>
            To exercise any right, email{" "}
            <a href="mailto:info@mitaict.com" className="text-[#D4AF37] hover:underline">info@mitaict.com</a>{" "}
            — we respond within 30 days and free of charge.
          </p>
        </Section>

        {/* 7. Security */}
        <Section icon={Lock} title="7. Security Measures">
          <p>Technical and organisational measures we take (Article 32 GDPR):</p>
          <ul className="space-y-1 pl-5 list-disc marker:text-[#D4AF37]">
            <li>HTTPS/TLS 1.3 encryption for all traffic</li>
            <li>Passwords hashed with bcrypt (never stored in plaintext)</li>
            <li>JWT authentication with HttpOnly, Secure, SameSite cookies</li>
            <li>Database access restricted by network isolation and strong credentials</li>
            <li>Third-party API keys rotated quarterly</li>
            <li>Breach notification: we notify affected users and the Swedish Integritetsskyddsmyndigheten (IMY) within 72 hours as required by Article 33 GDPR</li>
            <li>Regular dependency updates and security reviews</li>
          </ul>
        </Section>

        {/* 8. Children */}
        <Section icon={Shield} title="8. Children's Privacy">
          <p>
            StarFrame AI is not intended for children under the age of 16. We do not knowingly
            collect personal data from children. If you believe a child under 16 has created an
            account, please contact us and we will delete the account and all associated data
            within 14 days.
          </p>
          <p>
            This aligns with Article 8 GDPR and Sweden's national age threshold of 13 for
            information society services with parental consent.
          </p>
        </Section>

        {/* 9. Cookies */}
        <Section icon={Eye} title="9. Cookies and Tracking">
          <p><strong className="text-white">Strictly necessary cookies</strong> (no consent needed, ePrivacy Directive Art. 5(3) exemption):</p>
          <ul className="space-y-1 pl-5 list-disc marker:text-[#D4AF37]">
            <li><code className="text-xs bg-white/5 px-1.5 py-0.5 rounded">access_token</code>, <code className="text-xs bg-white/5 px-1.5 py-0.5 rounded">refresh_token</code> — authentication (HttpOnly, Secure, SameSite=None)</li>
          </ul>
          <p><strong className="text-white">Functional storage (localStorage):</strong> stores non-sensitive UI preferences like your user profile summary. You can clear it anytime via your browser settings.</p>
          <p>
            We do <strong className="text-white">not</strong> use advertising cookies, cross-site
            tracking, or profiling pixels. No third-party analytics (Google Analytics, Meta Pixel,
            etc.) are installed.
          </p>
        </Section>

        {/* 10. International Transfers */}
        <Section icon={Globe} title="10. International Data Transfers">
          <p>
            Some of our processors (Google, OpenAI, Stripe US, SendGrid, Cloudflare) are located
            outside the European Economic Area (EEA), primarily in the United States.
          </p>
          <p>
            For these transfers we rely on:
          </p>
          <ul className="space-y-1 pl-5 list-disc marker:text-[#D4AF37]">
            <li>European Commission Standard Contractual Clauses (SCC, 2021/914)</li>
            <li>EU-U.S. Data Privacy Framework where applicable</li>
            <li>Supplementary technical measures: end-to-end encryption where possible</li>
          </ul>
          <p>
            You can request a copy of the transfer safeguards by emailing us.
          </p>
        </Section>

        {/* 11. Complaints */}
        <Section icon={Scale} title="11. Right to Lodge a Complaint">
          <p>
            If you believe we have violated your data protection rights, you have the right to
            lodge a complaint with the <strong className="text-white">Swedish Data Protection
            Authority</strong> (Integritetsskyddsmyndigheten — IMY):
          </p>
          <ul className="space-y-1 pl-5 list-disc marker:text-[#D4AF37]">
            <li>Website: <a href="https://www.imy.se" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline">www.imy.se</a></li>
            <li>Email: <a href="mailto:imy@imy.se" className="text-[#D4AF37] hover:underline">imy@imy.se</a></li>
            <li>Address: Integritetsskyddsmyndigheten, Box 8114, 104 20 Stockholm, Sweden</li>
            <li>Phone: +46 (0)8 657 61 00</li>
          </ul>
          <p>
            We kindly ask you to contact us first at{" "}
            <a href="mailto:info@mitaict.com" className="text-[#D4AF37] hover:underline">info@mitaict.com</a>{" "}
            so we have the chance to resolve any issue promptly.
          </p>
        </Section>

        {/* 12. Changes */}
        <Section icon={Mail} title="12. Changes to This Policy">
          <p>
            We may update this Privacy Policy to reflect changes in our practices or legal
            requirements. Material changes will be communicated via email at least 14 days before
            taking effect, and the effective date at the top of this page will be updated.
          </p>
          <p>
            Continued use of the service after the effective date constitutes acceptance of the
            updated policy.
          </p>
        </Section>

        {/* 13. Deletion */}
        <Section icon={Trash2} title="13. How to Delete Your Account">
          <p>
            You can permanently delete your StarFrame AI account at any time:
          </p>
          <ol className="space-y-1 pl-5 list-decimal marker:text-[#D4AF37]">
            <li>Email <a href="mailto:info@mitaict.com?subject=Account%20Deletion%20Request" className="text-[#D4AF37] hover:underline">info@mitaict.com</a> from the address linked to your account.</li>
            <li>Subject line: "Account Deletion Request"</li>
            <li>We process the deletion within 30 days and send confirmation.</li>
          </ol>
          <p>
            All personal data will be erased, except records we are legally required to keep
            (payment records for 7 years per Swedish accounting law).
          </p>
        </Section>

        {/* Footer note */}
        <div className="mt-16 pt-8 border-t border-white/10 text-center">
          <p className="text-white/40 text-xs">
            Last updated: {effectiveDate} — MITA ICT AB — Stockholm, Sweden
          </p>
        </div>
      </div>
    </motion.div>
  );
};
